import {
  Bell,
  GraduationCap,
  LayoutDashboard,
  LogOut,
  MonitorUp,
  Search,
  Settings,
  ShieldCheck,
  TicketCheck
} from "lucide-react";
import { useRef, useState } from "react";
import type { Role } from "../shared/domain/index.js";
import { WebsitePopover } from "./WebsiteOverlay.js";
import type { AppRoute, AppSession, Workspace } from "./useSession.js";

function roleLabel(role: Role): string {
  if (role === "student") return "Student";
  if (role === "teacher") return "Docent";
  return "Beheer";
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "FD";
}

const simulatorRuntimeStorageKey = "firstdesk-v2-simulator-runtime-mode";
const legacySimulatorRuntimeStorageKey = "firstdesk-v2-native-simulator-mode";

function readStoredSimulatorMode() {
  if (typeof window === "undefined") return "fd-native";
  const legacyMode = window.localStorage.getItem(legacySimulatorRuntimeStorageKey);
  return window.localStorage.getItem(simulatorRuntimeStorageKey)
    ?? (legacyMode && legacyMode !== "native" ? legacyMode : null)
    ?? "fd-native";
}

export function AppTopbar({ session, onLogout }: { session: AppSession; onLogout: () => void }) {
  const [activeNav, setActiveNav] = useState("dashboard");
  const [noticeOpen, setNoticeOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [searchResults, setSearchResults] = useState<Array<{ id: string; title: string; subtitle: string }>>([]);
  const [noticeItems, setNoticeItems] = useState<Array<{ id: string; kind: string; createdAt: string }>>([]);
  const noticeRef = useRef<HTMLButtonElement>(null);
  const settingsRef = useRef<HTMLButtonElement>(null);
  const [simulatorMode, setSimulatorMode] = useState(readStoredSimulatorMode);

  const navItems = session.role === "teacher"
    ? [
      { key: "dashboard", label: "Dashboard", target: "teacher-overview" },
      { key: "groepen", label: "Groepen", target: "teacher-groups" },
      { key: "studenten", label: "Studenten", target: "teacher-students" },
      { key: "studentmodus", label: "Studentmodus", target: "teacher-overview" }
    ]
    : session.role === "student"
      ? [
        { key: "dashboard", label: "Dashboard", target: "dashboard" },
        { key: "tickets", label: "Tickets", target: "tickets" },
        { key: "student-results", label: "Resultaten", target: "student-results" }
      ]
      : [
        { key: "dashboard", label: "Dashboard", target: "admin-overview" },
        { key: "gebruikers", label: "Gebruikers", target: "admin-users" },
        { key: "instellingen", label: "Instellingen", target: "admin-settings" }
      ];

  function scrollToTarget(id: string) {
    setActiveNav(id);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  async function updateGlobalSearch(value: string) {
    setSearchValue(value);
    window.dispatchEvent(new CustomEvent("firstdesk:student-search", { detail: value }));
    if (value.trim().length >= 2) {
      try {
        const response = await fetch(`/api/v2/search?q=${encodeURIComponent(value.trim())}`, { credentials: "include" });
        const payload = await response.json() as { results?: Array<{ id: string; title: string; subtitle: string }> };
        setSearchResults(payload.results ?? []);
      } catch {
        setSearchResults([]);
      }
    } else {
      setSearchResults([]);
    }
    window.setTimeout(() => {
      const input = document.getElementById("fd-ticket-search") as HTMLInputElement | null;
      input?.focus();
    }, 0);
  }

  function updateSimulatorMode(value: string) {
    setSimulatorMode(value);
    window.localStorage.setItem(simulatorRuntimeStorageKey, value);
    window.localStorage.removeItem(legacySimulatorRuntimeStorageKey);
  }

  async function toggleNotifications() {
    const next = !noticeOpen;
    setNoticeOpen(next);
    setSettingsOpen(false);
    if (next) {
      try {
        const response = await fetch("/api/v2/notifications", { credentials: "include" });
        const payload = await response.json() as { items?: Array<{ id: string; kind: string; createdAt: string }> };
        setNoticeItems(payload.items ?? []);
      } catch {
        setNoticeItems([]);
      }
    }
  }

  return (
    <header className="fd-app-topbar">
      <div className="fd-app-topbar-left">
        <a className="fd-app-brand" href="#dashboard" aria-label="FirstDesk dashboard">FirstDesk</a>
        <nav className="fd-app-nav" aria-label="Hoofdnavigatie">
          {navItems.map((item) => (
            <button key={item.key} className={`fd-app-nav-link ${activeNav === item.key ? "fd-app-nav-link--active" : ""}`} type="button" onClick={() => scrollToTarget(item.target)}>{item.label}</button>
          ))}
        </nav>
      </div>
      <div className="fd-app-topbar-right">
        <label className="fd-app-search">
          <Search aria-hidden="true" size={16} />
          <input
            type="search"
            placeholder="Zoeken..."
            value={searchValue}
          onChange={(event) => void updateGlobalSearch(event.target.value)}
          onFocus={() => scrollToTarget("tickets")}
        />
      </label>
        <button
          className="fd-app-icon-btn"
          ref={noticeRef}
          type="button"
          aria-expanded={noticeOpen}
          aria-label="Meldingen"
          onClick={() => void toggleNotifications()}
        >
          <Bell aria-hidden="true" size={20} />
        </button>
        {session.role !== "student" ? (
          <button
            className="fd-app-icon-btn"
            ref={settingsRef}
            type="button"
            aria-expanded={settingsOpen}
            aria-label="Instellingen"
            onClick={() => {
              setSettingsOpen((current) => !current);
              setNoticeOpen(false);
            }}
          >
            <Settings aria-hidden="true" size={20} />
          </button>
        ) : null}
        {noticeOpen ? (
          <WebsitePopover anchorRef={noticeRef} open={noticeOpen} onClose={() => setNoticeOpen(false)} className="fd-topbar-popover" width={340}>
            <strong>Meldingen</strong>
            {noticeItems.length ? noticeItems.map((item) => (
              <p key={item.id}>{item.kind} · {new Date(item.createdAt).toLocaleString("nl-NL")}</p>
            )) : <p>Geen nieuwe meldingen. Je ticketfeedback verschijnt direct bij het ticket.</p>}
          </WebsitePopover>
        ) : null}
        {settingsOpen ? (
          <WebsitePopover anchorRef={settingsRef} open={settingsOpen} onClose={() => setSettingsOpen(false)} className="fd-topbar-popover fd-topbar-popover--settings" width={360}>
            <strong>Instellingen</strong>
            <p>Ingelogd als {session.displayName}. Kies tijdelijk de simulator-runtime voor QA; FirstDesk native is nu de standaardroute.</p>
            <label className="fd-topbar-setting-row">
              Simulator
              <select value={simulatorMode} onChange={(event) => updateSimulatorMode(event.target.value)}>
                <option value="fd-native">FirstDesk native</option>
                <option value="native">Native v2 classic</option>
                <option value="legacy">Legacy fallback</option>
                <option value="compare">Vergelijken</option>
              </select>
            </label>
          </WebsitePopover>
        ) : null}
        <span className="fd-app-user" title={session.email}>
          <span className="fd-app-avatar" aria-hidden="true">{initials(session.displayName)}</span>
          <span className="fd-app-user-meta">
            {session.role === "student" ? <a href="/student/profile"><strong>{session.displayName}</strong></a> : <strong>{session.displayName}</strong>}
            <small>{roleLabel(session.role)}</small>
          </span>
        </span>
        <button className="fd-app-icon-btn" type="button" aria-label="Uitloggen" onClick={onLogout}>
          <LogOut aria-hidden="true" size={20} />
        </button>
      </div>
      {searchResults.length ? (
        <div className="fd-topbar-search-results">
          {searchResults.map((item) => (
            <p key={item.id}><strong>{item.title}</strong><br />{item.subtitle}</p>
          ))}
        </div>
      ) : null}
    </header>
  );
}

export function Sidebar({
  onLogout,
  workspace,
  workspaces,
  onWorkspaceChange,
  onRouteChange
}: {
  onLogout: () => void;
  workspace: Workspace;
  workspaces: Workspace[];
  onWorkspaceChange: (workspace: Workspace) => void;
  onRouteChange: (route: AppRoute) => void;
}) {
  const [activeItem, setActiveItem] = useState("Mijn tickets");
  const sidebarTitle = workspace === "teacher" ? "ICT Academy" : workspace === "admin" ? "Beheercentrum" : "Talland College";
  const sidebarSub = workspace === "teacher" ? "Management Portal" : workspace === "admin" ? "Admin Portal" : "FirstDesk Omgeving";
  const roleItems = workspace === "teacher"
    ? [
      { label: "Overzicht", icon: LayoutDashboard, target: "teacher-overview" },
      { label: "Mijn studenten", icon: GraduationCap, target: "teacher-students" },
      { label: "Klasgroepen", icon: TicketCheck, target: "teacher-groups" },
      { label: "Studentmodus", icon: MonitorUp },
      { label: "Instellingen", icon: Settings, target: "teacher-settings" }
    ]
    : workspace === "admin"
      ? [
        { label: "Overzicht", icon: LayoutDashboard, target: "admin-overview" },
        { label: "Gebruikers", icon: ShieldCheck, target: "admin-users" },
        { label: "Ticketuitgifte", icon: TicketCheck, target: "admin-content" },
        { label: "Instellingen", icon: Settings, target: "admin-settings" }
      ]
      : [
        { label: "Mijn tickets", icon: TicketCheck, target: "tickets" },
        { label: "Afgesloten", icon: TicketCheck, target: "closed-tickets" },
        { label: "Profiel", icon: GraduationCap, target: "student-profile" },
        { label: "Groep aanmelden", icon: GraduationCap, target: "student-group" }
      ];

  return (
    <aside className="fd-app-sidebar">
      <div className="fd-sidebar-school">
        <div className="fd-sidebar-school-icon">
          <TicketCheck aria-hidden="true" size={20} />
        </div>
        <div className="fd-v2-teacher-side">
          <span className="fd-sidebar-school-name">{sidebarTitle}</span>
          <span className="fd-sidebar-school-sub">{sidebarSub}</span>
        </div>
      </div>

      <nav className="fd-sidebar-nav" aria-label="Werkruimte">
        {roleItems.map((item, index) => {
          const Icon = item.icon;
          const active = activeItem === item.label || (workspace !== "student" && index === 0 && activeItem === "Mijn tickets");
          return (
            <button
              className={`fd-sidebar-item ${active ? "fd-sidebar-item--active" : ""}`}
              key={item.label}
              type="button"
              onClick={() => {
                setActiveItem(item.label);
                if (item.label === "Studentmodus" && workspaces.includes("student")) {
                  onWorkspaceChange("student");
                }
                if (workspace === "student" && item.label === "Profiel") {
                  onRouteChange("student_profile");
                  window.history.pushState({}, "", "/student/profile");
                }
                if (workspace === "student" && item.label !== "Profiel") {
                  onRouteChange("dashboard");
                  if (window.location.pathname === "/student/profile") {
                    window.history.pushState({}, "", "/dashboard");
                  }
                }
                if ("target" in item && item.target) document.getElementById(item.target)?.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
            >
              <Icon aria-hidden="true" size={18} />
              <span>{item.label}</span>
            </button>
          );
        })}
      </nav>

      <div className="fd-sidebar-bottom">
        <button className="fd-sidebar-item fd-sidebar-logout" type="button" onClick={onLogout}>
          <LogOut aria-hidden="true" size={18} />
          <span>Uitloggen</span>
        </button>
        <div className="fd-sidebar-status">
          <span className="fd-sidebar-status-dot" />
          <span className="fd-sidebar-status-text">Online</span>
        </div>
      </div>
    </aside>
  );
}
